import {
  HOLDING_CONFIDENCE_LABELS,
  PRODUCT_HOLDING_STATUS_LABELS,
  type HoldingConfidence,
  type ProductHoldingStatus,
} from '@nova/shared';
import { CircleAlert, ShieldCheck, ShieldQuestion } from 'lucide-react';
import { cn } from '../../lib/cn.js';

const inputClass =
  'border-hairline bg-base/50 text-ink h-9 w-full rounded-lg border px-2.5 text-sm focus:border-accent/60 focus:outline-none';

export function Field({
  label,
  children,
  hint,
  className,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
  className?: string;
}) {
  return (
    <label className={cn('block', className)}>
      <span className="label-micro mb-1 block">{label}</span>
      {children}
      {hint && <span className="text-ink-soft/60 mt-1 block text-[10px]">{hint}</span>}
    </label>
  );
}

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cn(inputClass, props.className)} />;
}

export function SelectInput(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={cn(inputClass, 'appearance-none pr-8', props.className)} />;
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={cn(
        'border-hairline bg-base/50 text-ink w-full rounded-lg border px-2.5 py-2 text-sm focus:border-accent/60 focus:outline-none',
        props.className,
      )}
    />
  );
}

const STATUS_TONE: Record<ProductHoldingStatus, string> = {
  KUNDE: 'bg-success/15 text-success border-success/30',
  TIDLIGERE_KUNDE: 'bg-warning/12 text-warning border-warning/25',
  PROSPEKT: 'bg-accent/12 text-accent border-accent/25',
  KONKURRENT_KUNDE: 'bg-danger/12 text-danger border-danger/25',
  IKKE_RELEVANT: 'bg-ink-soft/10 text-ink-soft border-ink-soft/20',
};

export function HoldingStatusBadge({ status }: { status: ProductHoldingStatus }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
        STATUS_TONE[status],
      )}
    >
      {PRODUCT_HOLDING_STATUS_LABELS[status]}
    </span>
  );
}

/** Data-quality badges — so an «indikasjon» never reads as a verified fact. */
export function DataQualityBadges({
  confidence,
  liveVerified,
}: {
  confidence: HoldingConfidence | null;
  liveVerified: boolean;
}) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {liveVerified ? (
        <span className="text-success/90 inline-flex items-center gap-1 text-[11px]" title="Nettstedet er sjekket live">
          <ShieldCheck size={12} aria-hidden /> Live-verifisert
        </span>
      ) : (
        <span className="text-warning/90 inline-flex items-center gap-1 text-[11px]" title="Ikke verifisert live — regnes som en indikasjon">
          <ShieldQuestion size={12} aria-hidden /> Indikasjon
        </span>
      )}
      {confidence && (
        <span
          className={cn(
            'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px]',
            confidence === 'HOY'
              ? 'bg-success/10 text-success/90'
              : confidence === 'MIDDELS'
                ? 'bg-warning/10 text-warning/90'
                : 'bg-danger/10 text-danger/90',
          )}
          title="Konfidens i kartleggingen"
        >
          {HOLDING_CONFIDENCE_LABELS[confidence]}
        </span>
      )}
    </span>
  );
}

/**
 * Contract expiry — the single most important field. Colours the closer it gets:
 * red within 6 months, amber within 18, neutral otherwise; muted once passed.
 */
export function ExpiryBadge({
  contractEnd,
  daysToExpiry,
  formatted,
}: {
  contractEnd: string | null;
  daysToExpiry: number | null;
  formatted: string;
}) {
  if (!contractEnd || daysToExpiry === null) {
    return <span className="text-ink-soft/60 text-xs">Kontraktsutløp ukjent</span>;
  }
  const passed = daysToExpiry < 0;
  const months = Math.round(daysToExpiry / 30);
  const tone = passed
    ? 'text-ink-soft/60'
    : daysToExpiry <= 183
      ? 'text-danger'
      : daysToExpiry <= 548
        ? 'text-warning'
        : 'text-ink-soft';
  return (
    <span className={cn('inline-flex items-center gap-1 text-xs', tone)} title={`Kontraktsutløp ${formatted}`}>
      <CircleAlert size={12} aria-hidden />
      {passed
        ? `utløpt ${formatted}`
        : daysToExpiry <= 60
          ? `utløper om ${daysToExpiry} d`
          : `utløper om ${months} mnd`}
    </span>
  );
}
